import React, { useEffect, useState, useRef } from 'react';
import api from '../services/api';
import { Plus, Trash2, GripVertical, X, Check, ChevronUp, ChevronDown, ChevronRight, AlertTriangle, Trophy, Zap, Clock, CheckCircle2, Calendar, Hourglass } from 'lucide-react';

interface KanbanTask {
    id: string;
    title: string;
    done: boolean;
}

interface KanbanCard {
    id: string;
    title: string;
    description?: string;
    column: string;
    position: number;
    urgent: boolean;
    waitingApproval: boolean;
    deadline?: string;
    tasks: KanbanTask[];
}

const COLUMNS = ['Todo', 'In Progress', 'In Review', 'Done'];

const COL_CONFIG: Record<string, { label: string; badge: string; header: string; border: string; dot: string; icon: React.ReactNode; addBtn: string }> = {
    'Todo': {
        label: 'Todo',
        badge: 'bg-slate-100 text-slate-500',
        header: 'bg-slate-50 border-slate-200',
        border: 'border-slate-200',
        dot: 'bg-slate-400',
        icon: <Clock size={14} className="text-slate-400" />,
        addBtn: 'hover:bg-slate-100 text-slate-400 hover:text-slate-600 border-slate-200 hover:border-slate-300',
    },
    'In Progress': {
        label: 'In Progress',
        badge: 'bg-blue-100 text-blue-600',
        header: 'bg-blue-50 border-blue-200',
        border: 'border-blue-200',
        dot: 'bg-blue-500',
        icon: <Zap size={14} className="text-blue-500" />,
        addBtn: 'hover:bg-blue-50 text-blue-300 hover:text-blue-500 border-blue-200 hover:border-blue-300',
    },
    'In Review': {
        label: 'In Review',
        badge: 'bg-purple-100 text-purple-600',
        header: 'bg-purple-50 border-purple-200',
        border: 'border-purple-200',
        dot: 'bg-purple-500',
        icon: <CheckCircle2 size={14} className="text-purple-500" />,
        addBtn: 'hover:bg-purple-50 text-purple-300 hover:text-purple-500 border-purple-200 hover:border-purple-300',
    },
    'Done': {
        label: 'Done',
        badge: 'bg-emerald-100 text-emerald-600',
        header: 'bg-emerald-50 border-emerald-200',
        border: 'border-emerald-200',
        dot: 'bg-emerald-500',
        icon: <Trophy size={14} className="text-emerald-500" />,
        addBtn: 'hover:bg-emerald-50 text-emerald-300 hover:text-emerald-500 border-emerald-200 hover:border-emerald-300',
    },
};

const Kanban: React.FC = () => {
    const [cards, setCards] = useState<KanbanCard[]>([]);
    const [newTitle, setNewTitle] = useState<Record<string, string>>({});
    const [addingIn, setAddingIn] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editTitle, setEditTitle] = useState('');
    const [editDesc, setEditDesc] = useState('');
    const [editDeadline, setEditDeadline] = useState('');
    const [expandedTasks, setExpandedTasks] = useState<Record<string, boolean>>({});
    const [newTaskTitle, setNewTaskTitle] = useState<Record<string, string>>({});
    const [addingTaskIn, setAddingTaskIn] = useState<string | null>(null);
    const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
    const [editingTaskTitle, setEditingTaskTitle] = useState('');
    const dragCard = useRef<KanbanCard | null>(null);
    const dragOverCard = useRef<KanbanCard | null>(null);
    const [dragOverId, setDragOverId] = useState<string | null>(null);

    const fetchCards = async () => {
        const res = await api.get('/kanban');
        setCards(res.data);
    };

    useEffect(() => { fetchCards(); }, []);

    const addCard = async (column: string) => {
        const title = (newTitle[column] || '').trim();
        if (!title) return;
        await api.post('/kanban', { title, column });
        setNewTitle(p => ({ ...p, [column]: '' }));
        setAddingIn(null);
        fetchCards();
    };

    const deleteCard = async (id: string) => {
        await api.delete(`/kanban/${id}`);
        fetchCards();
    };

    const saveEdit = async (id: string) => {
        await api.put(`/kanban/${id}`, { title: editTitle, description: editDesc, deadline: editDeadline || null });
        setEditingId(null);
        fetchCards();
    };

    const toggleUrgent = async (card: KanbanCard) => {
        await api.put(`/kanban/${card.id}`, { urgent: !card.urgent });
        fetchCards();
    };

    const toggleWaitingApproval = async (card: KanbanCard) => {
        await api.put(`/kanban/${card.id}`, { waitingApproval: !card.waitingApproval });
        fetchCards();
    };

    const moveCard = async (card: KanbanCard, toColumn: string, toPosition?: number) => {
        if (card.column === toColumn && toPosition === undefined) return;
        await api.put(`/kanban/${card.id}`, { column: toColumn, position: toPosition ?? 999 });
        fetchCards();
    };

    const moveCardArrow = async (card: KanbanCard, direction: 'up' | 'down') => {
        const colCards = cards.filter(c => c.column === card.column).sort((a, b) => a.position - b.position);
        const idx = colCards.findIndex(c => c.id === card.id);
        if (direction === 'up' && idx > 0) {
            // Insert before the card above: use its position - 5 (midpoint between multiples of 10)
            await api.put(`/kanban/${card.id}`, { column: card.column, position: colCards[idx - 1].position - 5 });
            fetchCards();
        } else if (direction === 'down' && idx < colCards.length - 1) {
            // Insert after the card below: use its position + 5
            await api.put(`/kanban/${card.id}`, { column: card.column, position: colCards[idx + 1].position + 5 });
            fetchCards();
        }
    };

    const addTask = async (cardId: string) => {
        const title = (newTaskTitle[cardId] || '').trim();
        if (!title) return;
        await api.post(`/kanban/${cardId}/tasks`, { title });
        setNewTaskTitle(p => ({ ...p, [cardId]: '' }));
        setAddingTaskIn(null);
        fetchCards();
    };

    const toggleTask = async (taskId: string, done: boolean) => {
        await api.put(`/kanban/tasks/${taskId}`, { done: !done });
        fetchCards();
    };

    const deleteTask = async (taskId: string) => {
        await api.delete(`/kanban/tasks/${taskId}`);
        fetchCards();
    };

    const saveTaskEdit = async (taskId: string) => {
        const title = editingTaskTitle.trim();
        if (!title) return;
        await api.put(`/kanban/tasks/${taskId}`, { title });
        setEditingTaskId(null);
        fetchCards();
    };

    const onDragStart = (card: KanbanCard) => {
        dragCard.current = card;
    };

    const onDragOverCard = (e: React.DragEvent, card: KanbanCard) => {
        e.preventDefault();
        e.stopPropagation(); // prevent bubbling to column which resets dragOverCard
        dragOverCard.current = card;
        setDragOverId(card.id);
    };

    const onDropColumn = (col: string) => {
        const src = dragCard.current;
        const over = dragOverCard.current;
        if (!src) return;

        if (over && over.id !== src.id) {
            // Drop onto a specific card — insert before it
            moveCard(src, over.column, over.position - 5);
        } else {
            // Dropped on column background — move to end of that column
            moveCard(src, col, 999);
        }

        dragCard.current = null;
        dragOverCard.current = null;
        setDragOverId(null);
    };

    // Overall stats
    const totalCards = cards.length;
    const doneCards = cards.filter(c => c.column === 'Done').length;
    const totalTasks = cards.reduce((s, c) => s + c.tasks.length, 0);
    const doneTasks = cards.reduce((s, c) => s + c.tasks.filter(t => t.done).length, 0);
    const overallProgress = totalCards > 0 ? Math.round((doneCards / totalCards) * 100) : 0;
    const urgentCount = cards.filter(c => c.urgent).length;
    const waitingCount = cards.filter(c => c.waitingApproval).length;

    const motivationalMsg = overallProgress === 100
        ? "All done! You crushed it today!"
        : overallProgress >= 75 ? "Almost there — keep pushing!"
        : overallProgress >= 50 ? "Great momentum, halfway through!"
        : overallProgress >= 25 ? "Good start, keep the energy up!"
        : totalCards === 0 ? "Add your first card and get started!"
        : "Let's go — you've got this!";

    return (
        <div className="space-y-5">
            {/* Motivational Banner */}
            <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-xl p-5 text-white overflow-hidden">
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
                <div className="relative flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <p className="text-blue-100 text-sm font-medium mb-0.5">Today's Focus</p>
                        <h3 className="text-xl font-bold">{motivationalMsg}</h3>
                        <div className="flex items-center gap-4 mt-2 text-sm text-blue-100">
                            <span>{doneCards}/{totalCards} cards done</span>
                            {totalTasks > 0 && <span>{doneTasks}/{totalTasks} tasks</span>}
                            {urgentCount > 0 && <span className="flex items-center gap-1 text-red-300 font-medium"><AlertTriangle size={12} /> {urgentCount} urgent</span>}
                            {waitingCount > 0 && <span className="flex items-center gap-1 text-amber-300 font-medium"><Hourglass size={12} /> {waitingCount} awaiting</span>}
                        </div>
                    </div>
                    <div className="flex flex-col items-center">
                        <div className="relative w-16 h-16">
                            <svg className="w-16 h-16 -rotate-90" viewBox="0 0 60 60">
                                <circle cx="30" cy="30" r="24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="6" />
                                <circle cx="30" cy="30" r="24" fill="none" stroke="white" strokeWidth="6"
                                    strokeDasharray={`${2 * Math.PI * 24}`}
                                    strokeDashoffset={`${2 * Math.PI * 24 * (1 - overallProgress / 100)}`}
                                    strokeLinecap="round"
                                    style={{ transition: 'stroke-dashoffset 0.6s ease' }}
                                />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center text-white font-bold text-sm">{overallProgress}%</div>
                        </div>
                        <p className="text-xs text-blue-200 mt-1">Overall</p>
                    </div>
                </div>
                {/* Progress bar */}
                <div className="relative mt-3 h-1.5 bg-white/20 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-white rounded-full transition-all duration-700"
                        style={{ width: `${overallProgress}%` }}
                    />
                </div>
            </div>

            {/* Board */}
            <div className="grid grid-cols-4 gap-4">
                {COLUMNS.map(col => {
                    const cfg = COL_CONFIG[col];
                    const colCards = cards.filter(c => c.column === col).sort((a, b) => a.position - b.position);
                    const colTasks = colCards.reduce((s, c) => s + c.tasks.length, 0);
                    const colDone = colCards.reduce((s, c) => s + c.tasks.filter(t => t.done).length, 0);

                    return (
                        <div
                            key={col}
                            className={`rounded-xl border ${cfg.border} flex flex-col min-h-[300px]`}
                            onDragOver={e => { e.preventDefault(); dragOverCard.current = null; }}
                            onDrop={() => onDropColumn(col)}
                        >
                            {/* Column Header */}
                            <div className={`${cfg.header} border-b ${cfg.border} px-3 py-2.5 rounded-t-xl flex items-center justify-between`}>
                                <div className="flex items-center gap-2">
                                    {cfg.icon}
                                    <span className="text-xs font-semibold text-gray-700">{col}</span>
                                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${cfg.badge}`}>{colCards.length}</span>
                                </div>
                                <button onClick={() => setAddingIn(col)} className="text-gray-400 hover:text-blue-500 transition">
                                    <Plus size={15} />
                                </button>
                            </div>

                            {/* Task mini-progress for column */}
                            {colTasks > 0 && (
                                <div className="px-3 pt-2">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs text-gray-400">{colDone}/{colTasks} tasks</span>
                                        <span className="text-xs text-gray-400">{Math.round((colDone / colTasks) * 100)}%</span>
                                    </div>
                                    <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full transition-all duration-500 ${cfg.dot}`}
                                            style={{ width: `${Math.round((colDone / colTasks) * 100)}%` }}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="flex-1 p-2 space-y-2">
                                {colCards.map(card => {
                                    const tasksExpanded = expandedTasks[card.id];
                                    const doneCount = card.tasks.filter(t => t.done).length;
                                    const taskProgress = card.tasks.length > 0 ? Math.round((doneCount / card.tasks.length) * 100) : 0;
                                    return (
                                        <div
                                            key={card.id}
                                            draggable
                                            onDragStart={() => onDragStart(card)}
                                            onDragOver={e => onDragOverCard(e, card)}
                                            onDragLeave={() => { if (dragOverCard.current?.id === card.id) setDragOverId(null); }}
                                            onDrop={e => { e.stopPropagation(); onDropColumn(col); }}
                                            className={`bg-white border rounded-lg shadow-sm cursor-grab group transition-all hover:shadow-md ${
                                                dragOverId === card.id && dragCard.current?.id !== card.id
                                                    ? 'border-blue-400 shadow-blue-100 scale-[0.98]'
                                                    : card.urgent ? 'border-red-300' : card.waitingApproval ? 'border-amber-300' : 'border-gray-200'
                                            }`}
                                        >
                                            {/* Urgent top strip */}
                                            {card.urgent && <div className="h-0.5 bg-gradient-to-r from-red-400 to-orange-400 rounded-t-lg" />}
                                            {/* Waiting Approval top strip */}
                                            {!card.urgent && card.waitingApproval && <div className="h-0.5 bg-gradient-to-r from-amber-400 to-yellow-400 rounded-t-lg" />}

                                            {editingId === card.id ? (
                                                <div className="p-3 space-y-2">
                                                    <input
                                                        autoFocus
                                                        value={editTitle}
                                                        onChange={e => setEditTitle(e.target.value)}
                                                        className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                                    />
                                                    <textarea
                                                        value={editDesc}
                                                        onChange={e => {
                                                            setEditDesc(e.target.value);
                                                            e.target.style.height = 'auto';
                                                            e.target.style.height = e.target.scrollHeight + 'px';
                                                        }}
                                                        ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                                                        placeholder="Description..."
                                                        rows={1}
                                                        className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none resize-none overflow-hidden"
                                                    />
                                                    <div className="flex items-center gap-2">
                                                        <label className="text-xs text-gray-400 shrink-0">Deadline</label>
                                                        <input
                                                            type="date"
                                                            value={editDeadline}
                                                            onChange={e => setEditDeadline(e.target.value)}
                                                            className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                                        />
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <button onClick={() => saveEdit(card.id)} className="text-green-600 hover:text-green-700"><Check size={14} /></button>
                                                        <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="p-3">
                                                    <div className="flex items-start justify-between gap-2">
                                                        {/* Up/Down arrows — outside the edit-click area */}
                                                        <div className="shrink-0 flex flex-col items-center gap-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <button
                                                                onClick={() => moveCardArrow(card, 'up')}
                                                                className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                                                title="Move up"
                                                            >
                                                                <ChevronUp size={13} strokeWidth={2.5} />
                                                            </button>
                                                            <button
                                                                onClick={() => moveCardArrow(card, 'down')}
                                                                className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                                                title="Move down"
                                                            >
                                                                <ChevronDown size={13} strokeWidth={2.5} />
                                                            </button>
                                                        </div>
                                                        <div className="flex items-start gap-1 flex-1 min-w-0 cursor-pointer" onClick={() => { setEditingId(card.id); setEditTitle(card.title); setEditDesc(card.description || ''); setEditDeadline(card.deadline ? card.deadline.split('T')[0] : ''); }}>
                                                            <div className="min-w-0">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <p className="text-sm font-medium text-gray-800">{card.title}</p>
                                                                    {card.urgent && (
                                                                        <span className="inline-flex items-center gap-0.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full">
                                                                            <AlertTriangle size={9} /> Urgent
                                                                        </span>
                                                                    )}
                                                                    {card.waitingApproval && (
                                                                        <span className="inline-flex items-center gap-0.5 text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">
                                                                            <Hourglass size={9} /> Awaiting Approval
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {card.description && <p className="text-xs text-gray-400 mt-0.5 whitespace-pre-wrap break-words">{card.description}</p>}
                                                                {card.deadline && (() => {
                                                                    const diff = Math.ceil((new Date(card.deadline).getTime() - Date.now()) / 86400000);
                                                                    const overdue = diff < 0;
                                                                    const soon = diff >= 0 && diff <= 3;
                                                                    return (
                                                                        <span className={`inline-flex items-center gap-1 mt-1 text-xs font-medium px-1.5 py-0.5 rounded-full ${overdue ? 'bg-red-100 text-red-600' : soon ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-500'}`}>
                                                                            <Calendar size={10} />
                                                                            {overdue ? `${Math.abs(diff)}d overdue` : diff === 0 ? 'Today' : `${diff}d left`}
                                                                        </span>
                                                                    );
                                                                })()}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-0.5 shrink-0">
                                                            <button
                                                                onClick={() => toggleWaitingApproval(card)}
                                                                className={`transition ${card.waitingApproval ? 'text-amber-500' : 'opacity-0 group-hover:opacity-100 text-gray-300 hover:text-amber-400'}`}
                                                                title="Toggle awaiting client approval"
                                                            >
                                                                <Hourglass size={13} />
                                                            </button>
                                                            <button
                                                                onClick={() => toggleUrgent(card)}
                                                                className={`transition ${card.urgent ? 'text-red-500' : 'opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-400'}`}
                                                                title="Toggle urgent"
                                                            >
                                                                <AlertTriangle size={13} />
                                                            </button>
                                                            <button onClick={() => deleteCard(card.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition">
                                                                <Trash2 size={13} />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Task progress bar */}
                                                    {card.tasks.length > 0 && (
                                                        <div className="mt-2">
                                                            <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                                                                <div
                                                                    className={`h-full rounded-full transition-all duration-500 ${taskProgress === 100 ? 'bg-emerald-400' : 'bg-blue-400'}`}
                                                                    style={{ width: `${taskProgress}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Tasks toggle */}
                                                    <button
                                                        onClick={() => setExpandedTasks(p => ({ ...p, [card.id]: !p[card.id] }))}
                                                        className="mt-1.5 flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
                                                    >
                                                        {tasksExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                                        {card.tasks.length > 0
                                                            ? <span>{doneCount}/{card.tasks.length} tasks {taskProgress === 100 && '✓'}</span>
                                                            : <span>Tasks</span>
                                                        }
                                                    </button>
                                                </div>
                                            )}

                                            {/* Tasks List */}
                                            {tasksExpanded && (
                                                <div className="border-t border-gray-100 px-2 pb-2 pt-1.5 space-y-1">
                                                    {card.tasks.map(task => (
                                                        <div key={task.id} className={`flex items-center gap-2 group/task rounded-md px-2 py-1.5 transition-colors ${task.done ? 'bg-gray-50' : 'hover:bg-gray-50'}`}>
                                                            {/* Custom checkbox */}
                                                            <button
                                                                onClick={() => toggleTask(task.id, task.done)}
                                                                className={`shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${task.done ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 hover:border-blue-400'}`}
                                                            >
                                                                {task.done && <Check size={9} className="text-white" strokeWidth={3} />}
                                                            </button>
                                                            {editingTaskId === task.id ? (
                                                                <input
                                                                    autoFocus
                                                                    value={editingTaskTitle}
                                                                    onChange={e => setEditingTaskTitle(e.target.value)}
                                                                    onKeyDown={e => {
                                                                        if (e.key === 'Enter') saveTaskEdit(task.id);
                                                                        if (e.key === 'Escape') setEditingTaskId(null);
                                                                    }}
                                                                    onBlur={() => saveTaskEdit(task.id)}
                                                                    className="flex-1 text-xs border border-blue-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                                                />
                                                            ) : (
                                                                <span
                                                                    className={`text-xs flex-1 cursor-pointer select-none transition-all ${task.done ? 'line-through text-gray-400' : 'text-gray-700'}`}
                                                                    onDoubleClick={() => { setEditingTaskId(task.id); setEditingTaskTitle(task.title); }}
                                                                >
                                                                    {task.title}
                                                                </span>
                                                            )}
                                                            <button onClick={() => deleteTask(task.id)} className="opacity-0 group-hover/task:opacity-100 text-gray-300 hover:text-red-400 transition shrink-0">
                                                                <X size={11} />
                                                            </button>
                                                        </div>
                                                    ))}

                                                    {addingTaskIn === card.id ? (
                                                        <div className="flex gap-1 mt-1 px-1">
                                                            <input
                                                                autoFocus
                                                                value={newTaskTitle[card.id] || ''}
                                                                onChange={e => setNewTaskTitle(p => ({ ...p, [card.id]: e.target.value }))}
                                                                onKeyDown={e => { if (e.key === 'Enter') addTask(card.id); if (e.key === 'Escape') setAddingTaskIn(null); }}
                                                                placeholder="Task name..."
                                                                className="flex-1 text-xs border border-blue-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-blue-50"
                                                            />
                                                            <button onClick={() => addTask(card.id)} className="text-emerald-600 hover:text-emerald-700 p-1 rounded hover:bg-emerald-50"><Check size={13} /></button>
                                                            <button onClick={() => setAddingTaskIn(null)} className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"><X size={13} /></button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => { setAddingTaskIn(card.id); setExpandedTasks(p => ({ ...p, [card.id]: true })); }}
                                                            className="flex items-center gap-1 text-xs text-gray-400 hover:text-blue-500 mt-1 px-2 py-1 rounded-md hover:bg-blue-50 transition-colors w-full"
                                                        >
                                                            <Plus size={11} /> Add task
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Add card */}
                            <div className="px-2 pb-2">
                                {addingIn === col ? (
                                    <div className="space-y-2 bg-white border border-gray-200 rounded-lg p-2">
                                        <input
                                            autoFocus
                                            value={newTitle[col] || ''}
                                            onChange={e => setNewTitle(p => ({ ...p, [col]: e.target.value }))}
                                            onKeyDown={e => { if (e.key === 'Enter') addCard(col); if (e.key === 'Escape') setAddingIn(null); }}
                                            placeholder="Card title..."
                                            className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                        />
                                        <div className="flex gap-2">
                                            <button onClick={() => addCard(col)} className="flex-1 bg-blue-600 text-white text-xs py-1 rounded hover:bg-blue-700">Add</button>
                                            <button onClick={() => setAddingIn(null)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => setAddingIn(col)}
                                        className={`w-full text-xs py-1.5 border border-dashed rounded-lg transition ${cfg.addBtn}`}
                                    >
                                        + Add card
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default Kanban;
